import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Send, 
  Loader2, 
  User, 
  Phone, 
  Mail, 
  MessageSquare,
  Shield,
  AlertCircle,
  Check,
  Clock,
  ChevronDown,
  MapPin,
  Calendar,
  Lock,
  Share2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

export default function TripChat({ 
  match, 
  userId, 
  isOpen, 
  onClose,
  isPro = false 
}) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showContactShare, setShowContactShare] = useState(false);
  const [contactConsent, setContactConsent] = useState({
    share_email: false,
    share_whatsapp: false,
    share_phone: false,
    email: '',
    whatsapp: '',
    phone: ''
  });
  const [sharedContacts, setSharedContacts] = useState([]);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Load messages
  const loadMessages = useCallback(async () => {
    if (!match?.match_id || !isPro) return;
    
    try {
      const res = await api.getChatMessages(match.match_id, userId);
      if (res?.messages) {
        setMessages(res.messages);
      }
    } catch (error) {
      console.error('Load messages error:', error);
    } finally {
      setLoading(false);
    }
  }, [match?.match_id, userId, isPro]);
  
  // Load shared contacts
  const loadSharedContacts = useCallback(async () => {
    if (!match?.match_id || !isPro) return;
    
    try {
      const res = await api.getSharedContacts(match.match_id, userId);
      if (res?.contacts) {
        setSharedContacts(res.contacts);
      }
    } catch (error) {
      console.error('Load contacts error:', error);
    }
  }, [match?.match_id, userId, isPro]);
  
  useEffect(() => {
    if (isOpen && isPro) {
      loadMessages();
      loadSharedContacts();
      
      // Poll for new messages every 5 seconds
      const interval = setInterval(loadMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [isOpen, isPro, loadMessages, loadSharedContacts]);
  
  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    
    try {
      const res = await api.sendChatMessage({
        match_id: match.match_id,
        content: newMessage.trim()
      }, userId);
      
      if (res?.success) {
        setNewMessage('');
        // Add message to list immediately
        if (res.message) {
          setMessages(prev => [...prev, res.message]);
        }
        
        // Show moderation hint if flagged
        if (res.flagged) {
          toast.warning(res.moderation_hint || 'Your message was flagged for review');
        }
      } else if (res?.error === 'pro_required') {
        toast.error('Pro membership required to send messages');
      } else {
        toast.error(res?.error || 'Failed to send message');
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };
  
  // Update contact consent
  const handleUpdateConsent = async () => {
    try {
      const res = await api.updateContactConsent({
        match_id: match.match_id,
        ...contactConsent
      }, userId);
      
      if (res?.success) {
        toast.success('Contact preferences updated');
        setShowContactShare(false);
        loadSharedContacts();
      } else {
        toast.error(res?.error || 'Failed to update preferences');
      }
    } catch (error) {
      toast.error('Failed to update preferences');
    }
  };
  
  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };
  
  if (!isOpen) return null;
  
  // Pro Required View
  if (!isPro) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white shadow-2xl p-6 text-center"
            style={{ borderRadius: '1.5rem' }}
          >
            <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 flex items-center justify-center" style={{ borderRadius: '50%' }}>
              <Lock size={32} className="text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Pro Feature</h3>
            <p className="text-gray-600 mb-6">
              Upgrade to Pro to chat with your travel matches and coordinate trips together.
            </p>
            <a
              href="/payment"
              className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold shadow-lg hover:shadow-xl transition-all"
              style={{ borderRadius: '9999px' }}
            >
              Upgrade to Pro
            </a>
            <button
              onClick={onClose}
              className="block w-full mt-4 py-2 text-gray-500 hover:text-gray-700"
            >
              Maybe Later
            </button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-lg h-[80vh] bg-white shadow-2xl flex flex-col"
          style={{ borderRadius: '1.5rem' }}
        >
          {/* Header */}
          <div className="p-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-cyan-400 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                <MapPin size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">{match?.destination}</h3>
                <p className="text-xs text-gray-500">{match?.time_window}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowContactShare(!showContactShare)}
                className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                style={{ borderRadius: '0.5rem' }}
                title="Share Contact"
              >
                <Share2 size={20} />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                style={{ borderRadius: '0.5rem' }}
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Shared Contacts */}
          {sharedContacts.length > 0 && (
            <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-100 flex-shrink-0">
              <p className="text-xs text-emerald-700 font-medium mb-1">Shared contact info:</p>
              <div className="flex flex-wrap gap-2">
                {sharedContacts.map((contact, idx) => (
                  <React.Fragment key={idx}>
                    {contact.email && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-white text-emerald-700 text-xs" style={{ borderRadius: '9999px' }}>
                        <Mail size={12} />
                        {contact.email}
                      </span>
                    )}
                    {contact.whatsapp && (
                      <span className="flex items-center gap-1 px-2 py-0.5 bg-white text-emerald-700 text-xs" style={{ borderRadius: '9999px' }}>
                        <Phone size={12} />
                        {contact.whatsapp}
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
          
          {/* Contact Share Panel */}
          <AnimatePresence>
            {showContactShare && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-b border-gray-100 overflow-hidden flex-shrink-0"
              >
                <div className="p-4 bg-gray-50">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield size={16} className="text-teal-600" />
                    <span className="text-sm font-medium text-gray-700">Share your contact (optional)</span>
                  </div>
                  
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={contactConsent.share_email}
                        onChange={(e) => setContactConsent(prev => ({ ...prev, share_email: e.target.checked }))}
                        className="w-4 h-4 text-teal-600 rounded"
                      />
                      <Mail size={16} className="text-gray-400" />
                      <input
                        type="email"
                        value={contactConsent.email}
                        onChange={(e) => setContactConsent(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="Email"
                        className="flex-1 px-3 py-1.5 bg-white border border-gray-200 text-sm outline-none focus:border-teal-400"
                        style={{ borderRadius: '0.5rem' }}
                        disabled={!contactConsent.share_email}
                      />
                    </label>
                    
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={contactConsent.share_whatsapp}
                        onChange={(e) => setContactConsent(prev => ({ ...prev, share_whatsapp: e.target.checked }))}
                        className="w-4 h-4 text-teal-600 rounded"
                      />
                      <Phone size={16} className="text-gray-400" />
                      <input
                        type="tel"
                        value={contactConsent.whatsapp}
                        onChange={(e) => setContactConsent(prev => ({ ...prev, whatsapp: e.target.value }))}
                        placeholder="WhatsApp number"
                        className="flex-1 px-3 py-1.5 bg-white border border-gray-200 text-sm outline-none focus:border-teal-400"
                        style={{ borderRadius: '0.5rem' }}
                        disabled={!contactConsent.share_whatsapp}
                      />
                    </label>
                  </div>
                  
                  <button
                    onClick={handleUpdateConsent}
                    className="mt-3 w-full py-2 bg-teal-500 text-white text-sm font-semibold hover:bg-teal-600 transition-colors"
                    style={{ borderRadius: '0.5rem' }}
                  >
                    Save Preferences
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Safety Notice */}
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2 flex-shrink-0">
            <AlertCircle size={14} className="text-amber-600" />
            <p className="text-xs text-amber-700">
              Chat is trip-bound and auto-expires. Stay safe, be respectful.
            </p>
          </div>
          
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 size={32} className="animate-spin text-teal-500" />
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
                <MessageSquare size={48} className="mb-3 text-gray-300" />
                <p className="font-medium">No messages yet</p>
                <p className="text-sm">Start the conversation about your trip!</p>
              </div>
            ) : (
              messages.map((message) => {
                const isOwn = message.sender_id === userId;
                return (
                  <div
                    key={message.message_id}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[75%] ${isOwn ? 'order-2' : 'order-1'}`}>
                      <div
                        className={`px-4 py-2.5 ${
                          isOwn
                            ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white'
                            : 'bg-gray-100 text-gray-800'
                        } ${message.is_flagged ? 'opacity-60' : ''}`}
                        style={{ 
                          borderRadius: isOwn ? '1rem 1rem 0.25rem 1rem' : '1rem 1rem 1rem 0.25rem' 
                        }}
                      >
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        {message.is_flagged && (
                          <p className="text-xs mt-1 opacity-75 flex items-center gap-1">
                            <AlertCircle size={12} />
                            Under review
                          </p>
                        )}
                      </div>
                      <p className={`text-xs mt-1 text-gray-400 ${isOwn ? 'text-right' : 'text-left'}`}>
                        {formatTime(message.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input */}
          <div className="p-4 border-t border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Type a message..."
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-800 placeholder-gray-400 outline-none focus:bg-gray-50 transition-colors"
                style={{ borderRadius: '9999px' }}
                maxLength={1000}
              />
              <button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sending}
                className="p-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transition-all"
                style={{ borderRadius: '50%' }}
              >
                {sending ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <Send size={20} />
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
